import { getActiveProfileId, put } from './apiService';

// Log level constants
const FATAL = 'FATAL';
const ERROR = 'ERROR';
const WARN = 'WARN';
const INFO = 'INFO';
const DEBUG = 'DEBUG';
const TRACE = 'TRACE';

// Default log levels - which levels are enabled
const defaultLogLevels = {
  fatal: true,
  error: true,
  warn: true,
  info: true,
  debug: false,
  trace: false,
};

// Parse VITE_LOG_LEVELS from environment if provided
// Expected format: "fatal:true,error:true,warn:false,info:true,debug:false,trace:false"
function parseLogLevels(): typeof defaultLogLevels {
  const envLogLevels = import.meta.env.VITE_LOG_LEVELS;

  if (!envLogLevels) {
    return { ...defaultLogLevels };
  }

  try {
    const parsed = { ...defaultLogLevels };
    const pairs = envLogLevels.split(',');

    for (const pair of pairs) {
      const [level, value] = pair.split(':');
      const trimmedLevel = level.trim() as keyof typeof defaultLogLevels;
      const trimmedValue = value.trim().toLowerCase();

      if (trimmedLevel in parsed) {
        parsed[trimmedLevel] = trimmedValue === 'true';
      }
    }

    return parsed;
  } catch (error) {
    console.error('Failed to parse VITE_LOG_LEVELS, using defaults:', error);
    return { ...defaultLogLevels };
  }
}

const logLevels = parseLogLevels();

// Core logging function
function logLine(
  level: string,
  message: string,
  correlationId?: string,
  data?: unknown,
  error?: Error
) {
  const jsonMsg = {
    level,
    message,
    data: {
      time: Date.now(),
      message,
      data,
      correlationId,
      profileId: getActiveProfileId(),
      ...(error && { error: { name: error.name, message: error.message, stack: error.stack } }),
    },
  };
  const logToConsole = import.meta.env.VITE_LOG_CONSOLE === 'true';
  const logToCloud = import.meta.env.VITE_LOG_CLOUD === 'true';

  if (logToConsole) {
    console.log(JSON.stringify(jsonMsg));
  }

  if (logToCloud) {
    // Send to external logging service with cleaner structure
    const cloudMsg: Record<string, unknown> = {
      level,
      message,
      correlationId,
      profileId: getActiveProfileId(),
      time: Date.now(),
    };

    if (data) {
      cloudMsg.data = data;
    }

    if (error) {
      cloudMsg.error = {
        name: error.name,
        message: error.message,
        stack: error.stack
      };
    }

    put('/log', cloudMsg).catch((error) => {
      // Silently fail to avoid infinite logging loops
      console.error('Failed to send log to server:', error);
    });
  }
}

// Log functions
export function logFatal(error: Error, message: string, correlationId?: string, data?: unknown) {
  if (logLevels.fatal) {
    logLine(FATAL, message, correlationId, data, error);
  }
}

export function logError(error: Error, message: string, correlationId?: string, data?: unknown) {
  if (logLevels.error) {
    logLine(ERROR, message, correlationId, data, error);
  }
}

export function logWarn(message: string, correlationId?: string, data?: unknown) {
  if (logLevels.warn) {
    logLine(WARN, message, correlationId, data);
  }
}

export function logInfo(message: string, correlationId?: string) {
  if (logLevels.info) {
    logLine(INFO, message, correlationId);
  }
}

export function logDebug(message: string, correlationId?: string, data?: unknown) {
  if (logLevels.debug) {
    logLine(DEBUG, message, correlationId, data);
  }
}

export function logTrace(message: string, correlationId?: string, data?: unknown) {
  if (logLevels.trace) {
    logLine(TRACE, message, correlationId, data);
  }
}

// Function to update log levels at runtime
export function setLogLevel(level: keyof typeof logLevels, enabled: boolean) {
  logLevels[level] = enabled;
}

// Function to set all log levels at once (e.g., from user.logLevels)
export function setLogLevels(levels: typeof logLevels) {
  logLevels.fatal = levels.fatal;
  logLevels.error = levels.error;
  logLevels.warn = levels.warn;
  logLevels.info = levels.info;
  logLevels.debug = levels.debug;
  logLevels.trace = levels.trace;
}

// Convenience export
export const logger = {
  fatal: logFatal,
  error: logError,
  warn: logWarn,
  info: logInfo,
  debug: logDebug,
  trace: logTrace,
  setLogLevel,
  setLogLevels,
};
